import { getAccessToken } from 'Helpers/api';
import { authorizedFetch } from "Helpers/api";

let checkURL = process.env.REACT_APP_API_URL_PCI_COMPLIANCE as string;
const urlhost = window.location.host;
if (urlhost.includes('ev.')) {
    checkURL = 'https://pci-compliance.dev.venzagrc.com/api/pci-compliance';
}
if (urlhost.includes('tg.')) {
    checkURL = 'https://pci-compliance.stg.venzagrc.com/api/pci-compliance';
}
if (
    !urlhost.includes('ev.') &&
    !urlhost.includes('tg.') &&
    !urlhost.includes(':8')
) {
    checkURL = 'https://pci-compliance.prod.venzagrc.com/api/pci-compliance';
}

const baseUrl = checkURL as string;

// --- Authentication Helper ---
const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAccessToken()}`
});

export interface PropertyItem {
    property_id: number;
    property_name: string;
    status: 'notstarted' | 'incomplete' | 'pass' | 'fail';
    due_date: string | null; 
    last_change_date: string | null;
}

export interface PropertyFilterParams {
    tenant_id: number;
    provider_id: number;
    status?: string;
    property_name?: string;
    from_due_date?: string;
    to_due_date?: string;
    from_last_change_date?: string;
    to_last_change_date?: string;
    per_page?: number;
    page?: number;
    select_property_ids?: number[];
}

export interface VenzaPropertiesResponse {
    data: PropertyItem[];
    meta: {
        current_page: number;
        from: number;
        last_page: number;
        path: string;
        per_page: number;
        to: number;
        total: number;
    };
    links?: Record<string, string | null>;
    status: string;
}

export const formatMonth = (raw: string) => {
  const monthStr = raw.trim().slice(0, 7).replace("/", "-");  

  const [yearStr, monthStrNum] = monthStr.split("-");
  const year = Number(yearStr);
  const month = Number(monthStrNum);

  if (!year || month < 1 || month > 12) return raw;

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  return `${monthNames[month - 1]} '${year.toString().slice(2)}`;
};


// 1. Compliance Summary API
export const getComplianceSummary = async (
    tenantId: number,
    providerId: number
) => {
    const url = `${baseUrl}/compliance-summary?tenant_id=${tenantId}&provider_id=${providerId}`;

    try {
        const response = await authorizedFetch(url, { method: 'GET' });
        if (!response.ok) {
            throw {
                message: `API call failed with status: ${response.status}`,
                status: response.status,
                apiName: 'Compliance Summary'
            };
        }

        const result = await response.json();

        if (result && result.data) {
            return result.data;
        } else {
            throw new Error(
                "API response structure is invalid or missing 'data'."
            );
        }
    } catch (error) {
        if (error && (error as any).apiName) throw error;
        throw {
            message: (error as Error).message || 'Unknown error',
            status: 500,
            apiName: 'Compliance Summary'
        };
    }
};

// 2. Venza Properties API
export const getVenzaProperties = async (
    params: PropertyFilterParams
): Promise<VenzaPropertiesResponse> => {
    const validParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            validParams[key] = String(value);
        }
    });

    const searchParams = new URLSearchParams(validParams);
    const url = `${baseUrl}/properties?${searchParams.toString()}`;

    try {
        const response = await authorizedFetch(url, { method: 'GET' });
        if (!response.ok) {
            throw {
                message: `Property API call failed with status: ${response.status}`,
                status: response.status,
                apiName: 'Property List'
            };
        }

        const result: VenzaPropertiesResponse = await response.json();

        if (result && Array.isArray(result.data) && result.meta) {
            return result;
        } else {
            throw new Error(
                'Property API response structure is invalid or data/meta is missing.'
            );
        }
    } catch (error) {
        if (error && (error as any).apiName) throw error;
        throw {
            message: (error as Error).message || 'Unknown error',
            status: 500,
            apiName: 'Property List'
        };
    }
};

export interface ComplianceProgressItem {
    month: string;
    compliance_percentage: number;
}

export interface ProgressParams {
    tenant_id: number;
    provider_id: number;
    period: 'monthly' | 'yearly';
    from?: string;
    to?: string;
}

// 3. Compliance Progress API
export const getComplianceProgress = async (
    params: ProgressParams
): Promise<ComplianceProgressItem[]> => {
    const validParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            validParams[key] = String(value);
        }
    });

    const searchParams = new URLSearchParams(validParams);
    const url = `${baseUrl}/compliance-progress?${searchParams.toString()}`;

    try {
        const response = await authorizedFetch(url, { method: 'GET' });
        if (!response.ok) {
            throw {
                message: `Progress API call failed with status: ${response.status}`,
                status: response.status,
                apiName: 'Compliance Progress'
            };
        }

        const result = await response.json();

        if (result && Array.isArray(result.data)) {
            return result.data.map((item: any) => ({
                month: item.month || item.year,
                compliance_percentage: parseFloat(item.compliance_percentage)
            })) as ComplianceProgressItem[];
        } else {
            throw new Error(
                'Progress API response structure is invalid or data is not an array.'
            );
        }
    } catch (error) {
        if (error && (error as any).apiName) throw error;
        throw {
            message: (error as Error).message || 'Unknown error',
            status: 500,
            apiName: 'Compliance Progress'
        };
    }
};

// 4. Providers for Tenant API
export const getProvidersForTenant = async (
    tenantId: string,
    tenantName?: string
) => {
    let url = `${baseUrl}/tenants/providers?tenant_id=${tenantId}`;
    
    if (tenantName) {
        url += `&tenant_name=${tenantName}`; 
    }
    
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        if (!response.ok) {
            throw new Error(`API call failed: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching provider list:', error);
        throw error;
    }
};
